import { QueryClient, defaultShouldDehydrateQuery, type Query } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { Persister } from '@tanstack/react-query-persist-client';

/** How long a relaunch may reuse the last-known cache before it's considered stale
 *  on disk. We still re-subscribe to live data on mount, so this is just the window
 *  in which the app opens to *correct* values instead of empty placeholders. */
const CACHE_MAX_AGE = 1000 * 60 * 60 * 24; // 24h

/** Bump to invalidate every persisted cache at once (e.g. after a data-shape change).
 *  v2: League.cycling gained startsAt/endsAt (race window) for the lobby's date
 *  labels + hide-finished filter; old persisted leagues lack it. */
export const CACHE_BUSTER = 'h2h-cache-v2';

const CACHE_KEY = 'h2h-rq-cache';

/**
 * The single app-wide query client. `gcTime` is set to the persist window so cached
 * queries aren't garbage-collected out from under the persister between launches.
 * Live Firestore hooks (useLiveDoc / useLiveQuerySnap) keep `staleTime: Infinity`
 * and push fresh snapshots straight into this cache, so a restored value is shown
 * instantly and then silently refreshed when the listener reconnects.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      gcTime: CACHE_MAX_AGE
    }
  }
});

/**
 * localStorage-backed persister. localStorage survives WKWebView relaunches without
 * a native plugin; the cache is per-device and we clear it on sign-out (see
 * AuthContext) so a shared device never restores a previous account's data.
 */
const storagePersister =
  typeof window !== 'undefined'
    ? createSyncStoragePersister({ storage: window.localStorage, key: CACHE_KEY })
    : null;

/** A no-op persister for non-browser builds (e.g. SSR/tests) so the provider is safe. */
const noopPersister: Persister = {
  persistClient: async () => undefined,
  restoreClient: async () => undefined,
  removeClient: async () => undefined
};

export const persister: Persister = storagePersister ?? noopPersister;

export const persistOptions = {
  persister,
  maxAge: CACHE_MAX_AGE,
  buster: CACHE_BUSTER,
  dehydrateOptions: {
    // Persist only settled, successful reads (the RQ default). Per-fixture
    // community insight is bulky and cheap to refetch, so keep it off disk.
    shouldDehydrateQuery: (query: Query) =>
      defaultShouldDehydrateQuery(query) && query.queryKey[0] !== 'insight'
  }
};

/** Drop the in-memory cache AND its on-disk copy — used on sign-out so the next
 *  account on this device starts clean (entries are owner-private). */
export async function clearPersistedCache(): Promise<void> {
  queryClient.clear();
  try {
    await persister.removeClient();
  } catch {
    // Best-effort: a failed clear must never block sign-out.
  }
}
